import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { TrendingUp, Check, X } from 'lucide-react';
import api from '../../utils/api';
import { money, moneyK, fecha, Kpi, Title, Badge, Empty } from './ui';

const ESTADO = {
  activo: { label: 'Activo', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  pausado: { label: 'Pausado', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  baja: { label: 'Baja', color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
};

// Semáforo por antigüedad del último ajuste de precio
const semaforo = (dias) => {
  if (dias === null || dias === undefined) return { label: 'Sin registro', color: '#ef4444' };
  if (dias >= 180) return { label: `Hace ${Math.round(dias / 30)} meses`, color: '#ef4444' };
  if (dias >= 90) return { label: `Hace ${Math.round(dias / 30)} meses`, color: '#f59e0b' };
  return { label: dias < 31 ? 'Este mes' : `Hace ${Math.round(dias / 30)} meses`, color: '#22c55e' };
};

export default function AgenciaAbonos() {
  const [data, setData] = useState(null);
  const [editando, setEditando] = useState(null);   // id del contrato en edición
  const [valor, setValor] = useState('');
  const [pct, setPct] = useState('');
  const [filtro, setFiltro] = useState('activo');
  const navigate = useNavigate();

  const cargar = () => api.get('/agency/abonos').then(r => setData(r.data)).catch(() => toast.error('No se pudo cargar'));
  useEffect(() => { cargar(); }, []);

  const abrir = (a) => { setEditando(a.id); setValor(String(Math.round(Number(a.monto)))); setPct(''); };
  const aplicarPct = (a, p) => {
    setPct(p);
    const n = Number(p);
    if (!isNaN(n) && n) setValor(String(Math.round(Number(a.monto) * (1 + n / 100))));
  };
  const guardar = async (a) => {
    const monto = Number(valor);
    if (!monto || monto <= 0) return toast.error('Monto inválido');
    try {
      const { data: r } = await api.put(`/agency/contratos/${a.id}/precio`, { monto });
      toast.success(`${a.cliente}: ${money(r.anterior)} → ${money(r.nuevo)} (${r.porcentaje > 0 ? '+' : ''}${r.porcentaje}%)`);
      setEditando(null);
      cargar();
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo guardar'); }
  };

  if (!data) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const abonos = data.abonos.filter(a => filtro === 'todos' || a.estado === filtro);
  const pendientesAjuste = data.abonos.filter(a => a.estado === 'activo' && (a.dias_sin_ajuste === null || a.dias_sin_ajuste >= 90));
  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px' };
  const td = { padding: '8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const chip = (activo) => ({
    padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: '1px solid ' + (activo ? 'var(--accent)' : 'var(--border)'),
    background: activo ? 'var(--accent)' : 'transparent', color: activo ? '#fff' : 'var(--text-secondary)',
  });

  return (
    <div>
      <Title>Abonos mensuales</Title>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="Facturación mensual" value={moneyK(data.total_mensual)} sub={`${data.cantidad} abonos activos`} color="var(--accent)" />
        <Kpi label="Promedio por abono" value={moneyK(data.cantidad ? data.total_mensual / data.cantidad : 0)} />
        <Kpi label="Sin ajustar hace 3+ meses" value={pendientesAjuste.length} color={pendientesAjuste.length ? '#f59e0b' : 'var(--text-primary)'} sub="Candidatos a aumento" />
        <Kpi label="Proyección anual" value={moneyK(data.total_mensual * 12)} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {[['activo', 'Activos'], ['pausado', 'Pausados'], ['baja', 'Bajas'], ['todos', 'Todos']].map(([k, l]) => (
          <div key={k} style={chip(filtro === k)} onClick={() => setFiltro(k)}>{l}</div>
        ))}
      </div>

      <div className="card">
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Aumento asistido</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Ordenados por el tiempo que pasó desde el último ajuste. Tocá el monto para actualizarlo: el aumento queda registrado y se aplica al próximo cobro.
        </div>
        {abonos.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead><tr>
                <th style={th}>Cliente</th><th style={th}>Servicio</th><th style={th}>Día</th>
                <th style={th}>Último ajuste</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}></th>
              </tr></thead>
              <tbody>{abonos.map(a => {
                const s = semaforo(a.dias_sin_ajuste);
                const e = ESTADO[a.estado] || {};
                const enEdicion = editando === a.id;
                return (
                  <tr key={a.id}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate(`/agencia/clientes/${a.cliente_id}`)}>{a.cliente}</div>
                      {a.estado !== 'activo' && <Badge label={e.label} color={e.color} bg={e.bg} />}
                    </td>
                    <td style={{ ...td, color: 'var(--text-secondary)' }}>{a.nombre || a.servicio || 'Abono'}</td>
                    <td style={td}>{a.dia_cobro || 1}</td>
                    <td style={td}>
                      <span style={{ color: s.color, fontWeight: 600, fontSize: 12 }}>{s.label}</span>
                      {a.ultima_actualizacion_precio && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fecha(a.ultima_actualizacion_precio)}</div>}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {enEdicion ? (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                          {[10, 15, 20, 25].map(p => (
                            <span key={p} onClick={() => aplicarPct(a, p)}
                              style={{ ...chip(String(pct) === String(p)), padding: '3px 8px', fontSize: 11 }}>+{p}%</span>
                          ))}
                          <input autoFocus value={valor} onChange={e2 => { setValor(e2.target.value); setPct(''); }}
                            onKeyDown={e2 => { if (e2.key === 'Enter') guardar(a); if (e2.key === 'Escape') setEditando(null); }}
                            style={{ width: 120, textAlign: 'right' }} />
                        </div>
                      ) : (
                        <span onClick={() => abrir(a)} style={{ fontWeight: 700, cursor: 'pointer', borderBottom: '1px dashed var(--border)' }}>{money(a.monto)}</span>
                      )}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {enEdicion ? (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={() => guardar(a)} title="Guardar"><Check size={16} color="#22c55e" /></button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setEditando(null)} title="Cancelar"><X size={16} /></button>
                        </>
                      ) : (
                        <button className="btn btn-ghost btn-sm" onClick={() => abrir(a)} title="Ajustar precio"><TrendingUp size={16} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        ) : <Empty>No hay abonos en este estado</Empty>}
      </div>
    </div>
  );
}
